import * as React from 'react';
import moment from 'moment';
import { IconButton, Box, TextField, InputAdornment } from '@mui/material';
import { Payment, PaymentStatus } from '../../../models';
import { AutocompleteTextField } from './AutocompleteTextField';
import AddCircleIcon from '@material-ui/icons/AddCircleRounded';
import DeleteIcon from '@material-ui/icons/DeleteRounded';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { formatEuros } from '../../../shared/string';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { DateField } from './DateField';
import AppButton from '../../AppButton';

export interface MultiplePaymentsFieldProps {
    id?: string,
    label?: string,
    values: Payment[],
    maxValue?: number,
    disabled?: boolean,
    cashRegisterName?: string,
    onChange: (values: Payment[]) => void,
}

export const MultiplePaymentsField = (props: MultiplePaymentsFieldProps) => {
    const {
        id,
        label,
        values,
        maxValue,
        onChange,
        disabled,
        cashRegisterName,
    } = props;

    const t = useTranslations();
    const {paymentMethods} = useGeneralSettings();

    const onItemChanged = (idx: number, value: Payment) =>
        onChange(values.map((v, i) => i !== idx ? v : value));

    const total = values.filter(v => v.status === PaymentStatus.Success || v.status === PaymentStatus.Manual).map(v => v.value).reduce((p, c) => p + c, 0);
    const missingToPay = (maxValue ?? 0) - total;

    const showAddButton = !disabled && ((maxValue ?? Number.POSITIVE_INFINITY) - total > 0)

    const handleAddPayment = () => {
        const payment: any = {
            status: PaymentStatus.Manual,
            date: moment().format('YYYY-MM-DDTHH:mm'),
            type: paymentMethods.find(s => true) || '',
            value: maxValue ? maxValue - total : 0,
        };
        if (cashRegisterName) payment.cashRegisterName = cashRegisterName;
        onChange(values.concat(payment as Payment));
    };

    return (
        <div className="multipleInputsContainer">
            {label &&
                <label style={{ fontSize: '0.875rem', marginBottom: '8px', display: 'block', fontWeight: 500 }}>
                    {label}
                    {'\u00A0'}
                    {missingToPay > 0 && <span style={{fontSize: '0.85rem', color: 'red'}}>(<b>{t(TK.missing)}: {formatEuros(missingToPay)}</b>)</span>}
                    {'\u00A0'}
                    {showAddButton && <IconButton onClick={handleAddPayment} size="small"><AddCircleIcon/></IconButton>}
                </label>}
            {
                values.filter(p => p.status === PaymentStatus.Manual).map((payment, idx) => (
                    <div key={idx} className="multipleInputItem" style={{alignItems: 'center'}}>
                        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                            <Box flex="1" minWidth="150px"><DateField
                                id={id ? `${id}-${idx}-date` : undefined}
                                disabled={!!payment.id}
                                value={payment.date.substring(0, 16)}
                                type="datetime"
                                onChange={date => onItemChanged(idx, {...payment, date})}
                            /></Box>
                            <Box flex="1" minWidth="180px"><AutocompleteTextField
                                id={id ? `${id}-${idx}-type` : undefined}
                                value={payment.type}
                                options={paymentMethods}
                                onValueChanged={type => onItemChanged(idx, {...payment, type})}
                            /></Box>
                            <Box flex="0 0 120px">
                                <TextField
                                    id={id ? `${id}-${idx}-value` : undefined}
                                    disabled={!!payment.id}
                                    value={payment.value}
                                    onChange={(e) => onItemChanged(idx, {...payment, value: parseFloat(e.target.value) || 0})}
                                    type="number"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                    }}
                                />
                            </Box>
                        </Box>
                        { !disabled &&
                            <IconButton
                                id={id ? `${id}-${idx}-remove-button` : undefined }
                                size="small"
                                onClick={() => onChange(values.filter((_, i) => i !== idx))}
                                style={{color: '#dc3545'}}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        }
                    </div>
                ))
            }
        </div>
    );
}
